import { useState } from 'react';
import { Demand } from '../../../demandTypes';
import MainActionButton from '../../../../components/buttons/MainActionButton';
import { Dialog, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material';
import SecondaryActionButton from '../../../../components/buttons/SecondaryActionButton';
import { Form } from 'react-final-form';
import { FormMode, FormModeProvider } from '../../../../form/helper/FormModeProvider';
import { UploadSignedVerdictFormValues } from './uploadSignedVerdictFormTypes';
import { buildUploadSignedVerdictFormData } from './validateVerdictFormUtils';
import ApiHelper from '../../../../api/apiHelper';
import { captureException } from '@sentry/react';
import useSnackbar from '../../../../snackbar/useSnackbar';
import { Severity } from '../../../../utils/severityUtils';
import { SignedVerdictFileUploadFieldAdapter } from '../SignedVerdictFileUploadFieldAdapter';

type UploadSignedVerdictProps = {
  demand: Demand;
  refreshDemand: () => void;
};

export const UploadSignedVerdictButton = ({ demand, refreshDemand }: UploadSignedVerdictProps) => {
  const [dialogOpened, setDialogOpened] = useState(false);
  const [pending, setPending] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const closeDialog = () => {
    if (!pending) {
      setDialogOpened(false);
    }
  };

  const onSubmit = async (formValues: UploadSignedVerdictFormValues) => {
    const formData = buildUploadSignedVerdictFormData(formValues);
    try {
      setPending(true);
      await ApiHelper.put(`/api/demands/${demand.id}/verdict/signed-file`, formData);
      setDialogOpened(false);
      enqueueSnackbar({
        content: "L'avis signé a été ajouté avec succès",
        severity: Severity.SUCCESS,
      });
      refreshDemand();
    } catch (e) {
      captureException(e);
      enqueueSnackbar({
        content: "Une erreur est survenue pendant l'ajout de l'avis signé",
        severity: Severity.ERROR,
      });
    } finally {
      setPending(false);
    }
  };

  return (
    <>
      <MainActionButton
        dataTestId="upload-signed-verdict-button"
        label="Ajouter l'avis signé"
        action={() => setDialogOpened(true)}
      />
      <Dialog data-testid="upload-signed-verdict-dialog" open={dialogOpened} onClose={closeDialog} fullWidth>
        <FormModeProvider mode={FormMode.EDIT}>
          <Form onSubmit={onSubmit}>
            {({ handleSubmit }) => (
              <form onSubmit={handleSubmit}>
                <DialogTitle>Ajouter l'avis signé de la demande</DialogTitle>
                <DialogContent>
                  <Grid container>
                    <Grid item xs={12}>
                      <SignedVerdictFileUploadFieldAdapter disabled={pending} />
                    </Grid>
                  </Grid>
                </DialogContent>
                <DialogActions>
                  <SecondaryActionButton label="Annuler" action={closeDialog} disabled={pending} />
                  <MainActionButton
                    dataTestId="confirm-upload"
                    label="Confirmer"
                    type="submit"
                    disabled={pending}
                    pendingSpinner={pending}
                  />
                </DialogActions>
              </form>
            )}
          </Form>
        </FormModeProvider>
      </Dialog>
    </>
  );
};
